import { useCallback, useEffect, useMemo, useState } from 'react';

import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Autocomplete, { AutocompleteOption } from 'domains/core/components/Autocomplete';
import Button from 'domains/core/components/Button';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import useBulkReAssignRole from 'hooks/mutations/useBulkReAssignRole';
import useGetRoleById from 'hooks/queries/useGetRoleById';
import useGetRolesList from 'hooks/queries/useGetRolesList';
import useDebounce from 'hooks/useDebounce';
import { SEARCH_DEBOUNCE_MS } from 'utils';

import BulkRoleAutocompleteOption from './BulkRoleAutocompleteOption';

import type {
    AccessCentreRole,
    AccessCentreRolePermissionSummary,
    AccessCentreUser,
    BulkReAssignRoleApiResponse,
} from 'domains/accessCentre/types';

const ROLES_LIST_LIMIT = 100;

export type Props = {
    open: boolean;
    onClose: () => void;
    selectedCount: number;
    selectedUserIds: string[];
    /** Selected rows from the table; used to count how many already have the chosen role. */
    selectedUsers: AccessCentreUser[];
    onAssignSuccess: (data: BulkReAssignRoleApiResponse) => void;
};

const UsersBulkChangeRoleDialog = ({
    open,
    onClose,
    selectedCount,
    selectedUserIds,
    selectedUsers,
    onAssignSuccess,
}: Props) => {
    const theme = useTheme();
    const [roleInputValue, setRoleInputValue] = useState('');
    const [selectedOption, setSelectedOption] = useState<AutocompleteOption<AccessCentreRole> | null>(null);
    const [expandedRoleId, setExpandedRoleId] = useState<number | null>(null);
    const debouncedRoleSearch = useDebounce(roleInputValue, SEARCH_DEBOUNCE_MS);

    const { data: rolesData, isLoading: isRolesLoading } = useGetRolesList({
        limit: ROLES_LIST_LIMIT,
        offset: 0,
        searchTerm: debouncedRoleSearch.trim() || undefined,
        reactQueryOptions: { enabled: open },
    });

    const {
        data: expandedRoleByIdData,
        isLoading: isExpandedRoleByIdLoading,
        isError: isExpandedRoleByIdError,
    } = useGetRoleById(expandedRoleId ?? 0, {
        enabled: open && expandedRoleId !== null,
    });

    const expandedPermissionSummary: AccessCentreRolePermissionSummary | undefined = useMemo(() => {
        if (expandedRoleId === null || !expandedRoleByIdData) {
            return undefined;
        }
        const role = expandedRoleByIdData.role ?? expandedRoleByIdData.data?.role;
        if (!role || role.id !== expandedRoleId) {
            return undefined;
        }
        const nested = expandedRoleByIdData.data;
        return {
            module_count: expandedRoleByIdData.module_count ?? nested?.module_count ?? 0,
            permission_count: expandedRoleByIdData.permission_count ?? nested?.permission_count ?? 0,
            module_wise_permission_count:
                expandedRoleByIdData.module_wise_permission_count ?? nested?.module_wise_permission_count ?? [],
        };
    }, [expandedRoleByIdData, expandedRoleId]);

    const {
        mutate: bulkReAssignMutate,
        isLoading: isBulkAssignLoading,
        isError: isBulkAssignError,
        error: bulkAssignError,
        reset: resetBulkAssignMutation,
    } = useBulkReAssignRole();

    const roleOptions: AutocompleteOption<AccessCentreRole>[] = useMemo(
        () => (rolesData?.data ?? []).map((r) => ({ label: r.name, value: r })),
        [rolesData?.data]
    );

    const selectedUsersAlreadyOnChosenRoleCount = useMemo(() => {
        if (!selectedOption) {
            return 0;
        }
        const roleId = selectedOption.value.id;
        return selectedUsers.filter((u) => u.role_id === roleId).length;
    }, [selectedUsers, selectedOption]);

    useEffect(() => {
        if (open) {
            setRoleInputValue('');
            setSelectedOption(null);
            setExpandedRoleId(null);
            resetBulkAssignMutation();
        }
    }, [open, resetBulkAssignMutation]);

    useEffect(() => {
        if (selectedOption) {
            setRoleInputValue(selectedOption.label);
        }
    }, [selectedOption]);

    const handleInputChange = useCallback((_event: unknown, newInput: string, reason: string) => {
        if (reason === 'clear') {
            setSelectedOption(null);
            setRoleInputValue('');
            return;
        }
        if (reason === 'reset') {
            return;
        }
        setRoleInputValue(newInput);
    }, []);

    const handleAssign = () => {
        if (!selectedOption) {
            return;
        }
        bulkReAssignMutate(
            { userIds: selectedUserIds, roleId: selectedOption.value.id },
            {
                onSuccess: (data) => {
                    onAssignSuccess(data);
                    onClose();
                },
            }
        );
    };

    return (
        <>
            {isBulkAssignError && bulkAssignError && (
                <ErrorSnackbar errorMessage={bulkAssignError.message ?? 'Bulk assign failed'} />
            )}
            <Dialog
                maxWidth="sm"
                fullWidth
                open={open}
                onClose={onClose}
                data-testid="access-center-users-bulk-change-role-dialog"
                aria-labelledby="access-center-users-bulk-change-role-dialog-title"
            >
                <DialogTitle id="access-center-users-bulk-change-role-dialog-title">
                    <Typography variant="h6" component="span">
                        Change role
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ padding: '16px 32px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Assign a new role to {selectedCount} selected users.
                    </Typography>
                    <Stack spacing={0.5} sx={{ width: '100%' }}>
                        <Autocomplete
                            type="string"
                            componentsProps={{
                                popper: {
                                    sx: { zIndex: theme.zIndex.tooltip },
                                },
                            }}
                            options={roleOptions}
                            loading={isRolesLoading}
                            value={selectedOption}
                            onChange={(_, newValue) => setSelectedOption(newValue)}
                            inputValue={roleInputValue}
                            onInputChange={handleInputChange}
                            getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                            isOptionEqualToValue={(a, b) => a?.value?.id === b?.value?.id}
                            filterOptions={(opts) => opts}
                            noOptionsText={isRolesLoading ? 'Loading…' : 'No roles found'}
                            renderOption={(props, option) => {
                                const role = option.value;
                                const isExpanded = expandedRoleId === role.id;
                                return (
                                    <BulkRoleAutocompleteOption
                                        props={props}
                                        role={role}
                                        isExpanded={isExpanded}
                                        onToggleExpand={() =>
                                            setExpandedRoleId((prev) => (prev === role.id ? null : role.id))
                                        }
                                        details={isExpanded ? expandedPermissionSummary : undefined}
                                        isDetailsLoading={isExpanded && isExpandedRoleByIdLoading}
                                        isDetailsError={isExpanded && isExpandedRoleByIdError}
                                    />
                                );
                            }}
                            label="Choose Role"
                            placeholder="Select Role"
                            textFieldSx={{ mb: 0 }}
                            data-testid="access-center-users-bulk-change-role-autocomplete"
                        />
                        {selectedOption && selectedUsersAlreadyOnChosenRoleCount > 0 && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ textAlign: 'right', width: '100%', mt: 0 }}
                                data-testid="access-center-users-bulk-change-role-already-on-role-notice"
                            >
                                {selectedUsersAlreadyOnChosenRoleCount === 1
                                    ? "1 user already has this role and won't be updated"
                                    : `${selectedUsersAlreadyOnChosenRoleCount} users already have this role and won't be updated`}
                            </Typography>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="text" onClick={onClose} data-testid="access-center-users-bulk-change-role-cancel">
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!selectedOption || isBulkAssignLoading}
                        onClick={handleAssign}
                        data-testid="access-center-users-bulk-change-role-submit"
                    >
                        Assign Role
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UsersBulkChangeRoleDialog;
